/** @jsxImportSource @emotion/react */
import { css, SerializedStyles } from '@emotion/react';
import * as React from 'react';
import addCommentImg from '../../assets/icons/addComment.svg';
import calendarImg from '../../assets/icons/calendar.svg';
import commentImg from '../../assets/icons/comment.svg';
import copyImg from '../../assets/icons/copy.svg';
import flameImg from '../../assets/icons/flame.svg';
import pipImg from '../../assets/icons/greenDot.svg';
import historyImg from '../../assets/icons/history.svg';
import moreImg from '../../assets/icons/more.svg';
import { CONFIG_KEY } from '../../auth/config/configKey';
import ConfigHide from '../../auth/ConfigHide';
import { COOL_BLUE, COOL_GREY, WHITE } from '../../styles/colors';
import { UNSELECTABLE_TEXT } from '../../styles/styles';
import { getPreferredPass } from '../entitlements/module';
import { GuestDetails } from '../guestDetails';
import { OverflowMenuItem } from './OverflowMenuItem';
import { getResource } from '../../language/resources/getResource';


const OVERFLOW_MENU_BASE_HEIGHT  = 252;
const OVERFLOW_MENU_ENTRY_HEIGHT = 50;
const OVERFLOW_MENU_MAX_HEIGHT   = 302;

export const OVERFLOW_MENU_CTA_HEIGHT_PX = 33;
export const calcOverflowHeightPx        = (willShowAddToHotlist, willShowRemoveFromHotlist): number => {

  return Math.min(
    OVERFLOW_MENU_MAX_HEIGHT,
    OVERFLOW_MENU_BASE_HEIGHT
    + OVERFLOW_MENU_CTA_HEIGHT_PX
    + (willShowAddToHotlist ? OVERFLOW_MENU_ENTRY_HEIGHT : 0)
    + (willShowRemoveFromHotlist ? OVERFLOW_MENU_ENTRY_HEIGHT : 0),
  );
};

const OverflowMenuContainer = (overflowCss?: SerializedStyles) => [
  css`
    display: flex;
    flex-direction: column;
    min-height: ${OVERFLOW_MENU_CTA_HEIGHT_PX}px;
    background-color: ${WHITE};
    transition: all .3s;
  `,
  overflowCss, // Emotion will merge this if defined
];

const OverflowMenuOpenClose = css`
  width: 40px;
  height: 10px;
  margin: 12px auto;
  padding-right: 10px; /* Compensate for green pip */
  display: inline-block;
  ${UNSELECTABLE_TEXT};
`
const OverflowMenuPip = (opacity: string) => css`
  width: 10px; 
  height: 10px; 
  display: inline-block;
  margin-left: 5px;
  margin-top: 12px;
  margin-bottom: 12px;
  opacity: ${opacity};
  transition: all .5s ease;
  ${UNSELECTABLE_TEXT};
`

const OverflowMenuOptions = css`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow-y: auto; 
  flex-basis: 0;
`;

const OverflowMenuToggle = css`
  border-top: solid ${COOL_GREY} 1px;
  border-bottom: solid ${COOL_GREY} 1px;
  flex-basis: ${OVERFLOW_MENU_CTA_HEIGHT_PX}px;
  height: ${OVERFLOW_MENU_CTA_HEIGHT_PX}px;
  flex-grow: 0;
  flex-shrink: 0;
  display: flex;
  &:hover {
    background-color: ${COOL_BLUE}; 
    cursor: pointer; 
  }
`;

/**
 * GuestCardOverflowMenu: a component that renders all the calls to action that didn't fit on the face of the Guest Card.
 * these are all the tertiary actions like Copy Guest ID and View Days.
 *
 * This menu has hard-coded heights to support animation, be sure to update the constants at the top of the file when
 * you want to change the menu height. While the menu does its little expansion trick, this component doesn't handle
 * that; guest card handles expand/contract animation/logic.
 *
 * PureComponent: true
 * Connected to Redux: false
 */
export default class GuestCardOverflowMenu extends React.PureComponent<{
  guest: GuestDetails;
  enableGlobalHotlist: boolean;
  enableResortSpecificHotlist: boolean;
  hasHotlistablePass: boolean;
  hasHotlistedPass: boolean;
  hasExpiredHotlistedPass: boolean;
  passEntitlementId: number;
  hasHotlistHistory: boolean;
  rootCss?: SerializedStyles;
  resources: Record<string, any>;
  onOverflowButtonClick?: () => void;
  onViewDaysClick: () => void;
  onCopyMasterGuestIdClick: () => void;
  onAddToHotlistClick: () => void;
  onRemoveFromHotlistClick: () => void;
  onViewCommentsClick: () => void;
  onAddCommentClick: () => void;
  onMediaHistoryClick: () => void;
}> {
  public onOverflowButtonClick = () => {
    if (this.props.onOverflowButtonClick) {
      this.props.onOverflowButtonClick();
    }
  };


  public onCopyMasterGuestIdClick = () => this.props.onCopyMasterGuestIdClick();
  public onViewDaysClick          = () => this.props.onViewDaysClick();
  public onAddToHotlistClick      = () => this.props.onAddToHotlistClick();
  public onRemoveFromHotlistClick = () => this.props.onRemoveFromHotlistClick();
  public onViewCommentsClick      = () => this.props.onViewCommentsClick();
  public onAddCommentClick        = () => this.props.onAddCommentClick();
  public onMediaHistoryClick      = () => this.props.onMediaHistoryClick();


  public render = () => {
    const guestName       = `${this.props.guest.firstName} ${this.props.guest.lastName}`;
    const hasCurrentMedia = !!this.props.guest.media;
    const hasPass         = !!getPreferredPass(this.props.passEntitlementId, this.props.guest);

    return (
      <div
        css={OverflowMenuContainer(this.props.rootCss)}
      >
        <div
          onClick={this.onOverflowButtonClick}
          title={getResource(this.props.resources, 'GUEST_CARD_OVERFLOW_TOGGLE')}
          css={OverflowMenuToggle}
        >
          <img
            src={pipImg}
            alt='•'
            css={OverflowMenuPip(!!this.props.guest.hasComments || this.props.hasHotlistHistory ? '1' : '0')}
          />
          <img
            src={moreImg}
            alt='Open/Close Overflow Menu'
            css={OverflowMenuOpenClose}
          />
        </div>
        <div
          css={OverflowMenuOptions}
        >
          {/* Copy Master Guest ID */}
          <OverflowMenuItem
            imgSrc={copyImg}
            onMenuItemClick={this.onCopyMasterGuestIdClick}
            title={this.props.guest.masterGuestId}
            label={getResource(this.props.resources, 'GUEST_CARD_OVERFLOW_COPY_MGID')}
          />

          {/* View Days */}
          <OverflowMenuItem
            imgSrc={calendarImg}
            onMenuItemClick={this.onViewDaysClick}
            title={hasPass ? getResource(this.props.resources, 'GUEST_CARD_OVERFLOW_GUEST_HAS_PASS') : getResource(this.props.resources, 'GUEST_CARD_OVERFLOW_GUEST_NO_PASS')}
            label={getResource(this.props.resources, 'GUEST_CARD_OVERFLOW_VIEW_DAYS')}
          />

          {/* Media Card History */}
          <OverflowMenuItem
            imgSrc={historyImg}
            disabled={!hasCurrentMedia}
            onMenuItemClick={this.onMediaHistoryClick}
            title={getResource(this.props.resources, 'GUEST_CARD_OVERFLOW_MEDIA_HISTORY_TITLE', guestName)}
            label={getResource(this.props.resources, 'GUEST_CARD_OVERFLOW_MEDIA_HISTORY')}
          />

          { // Hotlist Add
            <ConfigHide configPath={CONFIG_KEY.ENABLE_ADD_TO_HOTLIST}>
              <OverflowMenuItem
                imgSrc={flameImg}
                disabled={(!this.props.enableGlobalHotlist && !this.props.enableResortSpecificHotlist) || (!hasPass || !this.props.hasHotlistablePass)}
                onMenuItemClick={this.onAddToHotlistClick}
                title={hasPass && this.props.hasHotlistablePass
                  ? getResource(this.props.resources, 'GUEST_CARD_OVERFLOW_PUT_HOTLIST')
                  : hasPass
                    ? getResource(this.props.resources, 'GUEST_CARD_OVERFLOW_ALREADY_HOTLISTED')
                    : getResource(this.props.resources, 'GUEST_CARD_OVERFLOW_NO_PASS_HOTLIST')
                }
                label={getResource(this.props.resources, 'GUEST_CARD_OVERFLOW_HOTLIST')}
              />
            </ConfigHide>
          }

          { // Hotlist Remove
            (this.props.hasHotlistedPass || this.props.hasExpiredHotlistedPass)
            && <ConfigHide configPath={CONFIG_KEY.ENABLE_REMOVE_FROM_HOTLIST}>
              <OverflowMenuItem
                imgSrc={flameImg}
                onMenuItemClick={this.onRemoveFromHotlistClick}
                title={getResource(this.props.resources, 'GUEST_CARD_OVERFLOW_TAKE_HOTLIST')}
                label={getResource(this.props.resources, 'GUEST_CARD_OVERFLOW_REMOVE_HOTLIST')}
              />
            </ConfigHide>
          }

          {/* View Comments */}
          <OverflowMenuItem
            imgSrc={commentImg}
            onMenuItemClick={this.onViewCommentsClick}
            title={getResource(this.props.resources, 'GUEST_CARD_OVERFLOW_COMMENT_HISTORY', guestName)}
            showNotificationPip={!!this.props.guest.hasComments}
            label={getResource(this.props.resources, 'GUEST_CARD_OVERFLOW_VIEW_COMMENTS')}
          />

          {/* Add Comment */}
          <OverflowMenuItem
            imgSrc={addCommentImg}
            onMenuItemClick={this.onAddCommentClick}
            title={getResource(this.props.resources, 'GUEST_CARD_OVERFLOW_ADD_COMMENT_TITLE', guestName)}
            label={getResource(this.props.resources, 'GUEST_CARD_OVERFLOW_ADD_COMMENT')}
          />
        </div>
      </div>
    );
  };
}
